import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { ALL_KEY, RAW_DATE_FORMAT } from '@/common/constants';
import { ORDERS } from '@/common/paths';
import { Pagination } from '@/common/types';
import {
  InvestmentActivitySide,
  InvestmentOrderType,
  TransactionHistoryPayload,
  TransactionStatus,
} from '@/modules/investments/investments.types';

export const QUERY_KEY = 'GET_TRANSACTIONS_HISTORY';

enum TransactionInstrumentStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  CloseOnly = 'CLOSE_ONLY',
  Halted = 'HALTED',
}

interface Response {
  amount: number;

  date_time: string;
  full_liquidation: boolean;
  fund_name: string;
  image_url: string;
  instrument_status: TransactionInstrumentStatus;
  order_type: InvestmentOrderType;
  research_url: string;
  shares: number;
  side: InvestmentActivitySide;
  status: TransactionStatus;
  symbol: string;
  type: string;
}

interface Params {
  page: number;
  perPage: number;
  amount?: string;
  startDate: Date | null;
  endDate: Date | null;
  funds: string[];
}

const useTransactionsHistoryQuery = (params?: Params) => {
  const { isLoading, isSuccess, data } = useQuery(
    [
      QUERY_KEY,
      params?.page,
      params?.perPage,
      params?.amount,
      params?.startDate,
      params?.endDate,
      ...(params?.funds || []),
    ],
    () =>
      // TODO: type is not exists
      api.get<Pagination<Response>>(ORDERS, {
        params: _.pickBy({
          sort: 'createdAt,desc',
          page: params?.page ? params?.page - 1 : undefined,
          size: params?.perPage,
          instrument_ids: params?.funds.length ? params.funds.join(',') : undefined,
          start_date: params?.startDate ? dayjs(params?.startDate).format(RAW_DATE_FORMAT) : null,
          end_date: params?.endDate ? dayjs(params?.endDate).format(RAW_DATE_FORMAT) : null,
          min_amount: !!params?.amount && params.amount !== ALL_KEY ? _.toNumber(params.amount) : undefined,
        }),
      })
  );

  const formattedData = useMemo(() => {
    const list = data?.data?.content || [];
    return list.map((item, index) => {
      const formattedItem: TransactionHistoryPayload = {
        id: `${item.symbol}_${index}`,
        symbol: item.symbol,
        name: item.fund_name,
        type: item.type,
        shares: item.shares,
        date: dayjs(item.date_time),
        status: item.status,
        image: item.image_url,
        amount: item.amount,
        side: item.side,
      };
      return formattedItem;
    });
  }, [data?.data?.content]);
  const total = useMemo(() => data?.data?.totalElements || 0, [data?.data?.totalElements]);
  const isEmpty = useMemo(() => total === 0 && isSuccess, [isSuccess, total]);
  return {
    isLoading,
    isEmpty,
    total,
    isSuccess,
    data: formattedData,
  };
};

export default useTransactionsHistoryQuery;
